import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  ListGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";
import { apiClient } from "../../../api/client";
import { useMemberships } from "../../billing/hooks/useMemberships";

const INITIAL_NEW = { name: "", email: "" };

function useInviteOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post("/api/v1/onboarding/owners/", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memberships", "OWNER"] }),
  });
}

// Promueve a un docente/estudiante existente a copropietario — aditivo, no
// toca su(s) rol(es) previos (ver MembershipElevationSerializer en el backend).
function usePromoteToOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email) =>
      apiClient
        .post("/api/v1/memberships/", { user_email: email, role: "OWNER" })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", "OWNER"] });
      qc.invalidateQueries({ queryKey: ["memberships", "TEACHER"] });
      qc.invalidateQueries({ queryKey: ["memberships", "STUDENT"] });
    },
  });
}

function useDemoteOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId) =>
      apiClient
        .patch(`/api/v1/memberships/${membershipId}/`, { is_active: false })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memberships", "OWNER"] }),
  });
}

function apiErr(err, fallback) {
  const data = err?.response?.data;
  return (
    data?.email?.[0] ??
    data?.user_email?.[0] ??
    data?.message ??
    data?.detail ??
    fallback
  );
}

function PropietariosPage() {
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(INITIAL_NEW);

  // Promote (asignar propietario a usuario existente) — con typeahead.
  const [showPromote, setShowPromote] = useState(false);
  const [promoteQuery, setPromoteQuery] = useState("");
  const [promoteSelected, setPromoteSelected] = useState(null); // { id, email, name }

  const { data: owners, isLoading, isError } = useMemberships("OWNER");
  const { data: teachers } = useMemberships("TEACHER");
  const { data: students } = useMemberships("STUDENT");

  const inviteMutation = useInviteOwner();
  const promoteMutation = usePromoteToOwner();
  const demoteMutation = useDemoteOwner();

  const ownerUserIds = useMemo(
    () => new Set((owners ?? []).map((m) => m.user?.id)),
    [owners]
  );
  // No se puede quitar al único propietario que queda (el backend lo
  // rechaza con 409 de todas formas — esto solo evita el viaje redondo
  // garantizado a fallar).
  const isLastOwner = (owners ?? []).length <= 1;

  // Candidatos a promover: docentes o estudiantes que aún no son propietarios,
  // filtrados por texto. Un mismo usuario puede aparecer en ambas listas
  // (rol dual) — se deduplica por id.
  const promoteCandidates = useMemo(() => {
    const q = promoteQuery.trim().toLowerCase();
    const seen = new Set();
    return [...(teachers ?? []), ...(students ?? [])]
      .filter((m) => !ownerUserIds.has(m.user?.id))
      .filter((m) => {
        if (!m.user?.id || seen.has(m.user.id)) return false;
        seen.add(m.user.id);
        return true;
      })
      .map((m) => ({
        id: m.user?.id,
        email: m.user?.email ?? "",
        name: m.user?.full_name ?? "",
      }))
      .filter(
        (c) =>
          !q ||
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [teachers, students, ownerUserIds, promoteQuery]);

  const setNewField = (field) => (e) =>
    setNewForm((prev) => ({ ...prev, [field]: e.target.value }));

  const resetPromote = () => {
    setShowPromote(false);
    setPromoteQuery("");
    setPromoteSelected(null);
  };

  const handleCreateNew = (e) => {
    e.preventDefault();
    inviteMutation.mutate(newForm, {
      onSuccess: () => {
        toast.success(
          `Propietario agregado. Se envió la contraseña temporal a ${newForm.email}.`
        );
        setShowNew(false);
        setNewForm(INITIAL_NEW);
      },
      onError: (err) => toast.error(apiErr(err, "Error al invitar al propietario.")),
    });
  };

  const handlePromote = (e) => {
    e.preventDefault();
    if (!promoteSelected) return;
    promoteMutation.mutate(promoteSelected.email, {
      onSuccess: () => {
        toast.success(
          `${promoteSelected.name || promoteSelected.email} ahora es copropietario.`
        );
        resetPromote();
      },
      onError: (err) => toast.error(apiErr(err, "No se pudo asignar el rol.")),
    });
  };

  const handleDemote = (m) => {
    const label = m.user?.full_name || m.user?.email || "este propietario";
    if (!window.confirm(`¿Quitar a ${label} como propietario?`)) return;
    demoteMutation.mutate(m.id, {
      onSuccess: () => toast.success("Propietario removido."),
      onError: (err) => toast.error(apiErr(err, "No se pudo quitar al propietario.")),
    });
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <h4 className="mb-0">Propietarios</h4>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="outline-primary"
              style={{ minHeight: 44 }}
              onClick={() => setShowPromote(true)}
            >
              Asignar usuario existente
            </Button>
            <Button
              variant="primary"
              style={{ minHeight: 44 }}
              onClick={() => setShowNew(true)}
            >
              + Nuevo propietario
            </Button>
          </div>
        </div>
        <p className="text-muted small mb-3">
          Comparte el control de tu academia con un copropietario: invita a una
          persona nueva, o asigna el rol a un docente/estudiante que ya
          pertenece a tu academia sin crear otra cuenta. No hay límite de
          cuántos propietarios puede tener tu academia.
        </p>

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {isError && (
          <Alert variant="danger">No se pudieron cargar los propietarios.</Alert>
        )}

        {!isLoading && !isError && (
          <>
            {/* Desktop table */}
            <div className="d-none d-sm-block">
              <Card>
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Nombre</th>
                        <th>Correo</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(owners ?? []).length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No hay propietarios registrados aún.
                          </td>
                        </tr>
                      ) : (
                        (owners ?? []).map((m) => (
                          <tr key={m.id}>
                            <td>{m.user?.full_name || "—"}</td>
                            <td>{m.user?.email}</td>
                            <td>
                              <Badge bg={m.is_active ? "success" : "secondary"}>
                                {m.is_active ? "Activo" : "Inactivo"}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ minHeight: 44 }}
                                onClick={() => handleDemote(m)}
                                disabled={demoteMutation.isPending || isLastOwner}
                                title={
                                  isLastOwner
                                    ? "Asigna otro propietario antes de quitar este."
                                    : undefined
                                }
                              >
                                Quitar propietario
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </div>

            {/* Mobile cards */}
            <div className="d-sm-none d-flex flex-column gap-3">
              {(owners ?? []).length === 0 && (
                <p className="text-muted text-center py-3">
                  No hay propietarios registrados aún.
                </p>
              )}
              {(owners ?? []).map((m) => (
                <Card key={m.id}>
                  <Card.Body>
                    <div className="fw-medium">{m.user?.full_name || "—"}</div>
                    <div className="text-muted small">{m.user?.email}</div>
                    <div className="mt-2 mb-2">
                      <Badge bg={m.is_active ? "success" : "secondary"}>
                        {m.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <Button
                      variant="outline-danger"
                      className="w-100"
                      style={{ minHeight: 44 }}
                      onClick={() => handleDemote(m)}
                      disabled={demoteMutation.isPending || isLastOwner}
                    >
                      Quitar propietario
                    </Button>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal: nuevo propietario (cuenta nueva) */}
      <Modal show={showNew} onHide={() => setShowNew(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Agregar nuevo propietario</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateNew}>
          <Modal.Body>
            <div className="d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label className="fw-medium">
                  Nombre completo <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ana Ruiz"
                  value={newForm.name}
                  onChange={setNewField("name")}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Correo electrónico <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="propietario@ejemplo.com"
                  value={newForm.email}
                  onChange={setNewField("email")}
                  required
                />
                <Form.Text className="text-muted">
                  Recibirá un correo con una contraseña temporal que deberá
                  cambiar al entrar.
                </Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              style={{ minHeight: 44 }}
              onClick={() => setShowNew(false)}
              disabled={inviteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ minHeight: 44 }}
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Agregar propietario"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: asignar rol a usuario existente (typeahead) */}
      <Modal show={showPromote} onHide={resetPromote} centered>
        <Modal.Header closeButton>
          <Modal.Title>Asignar propietario a usuario existente</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePromote}>
          <Modal.Body>
            <p className="text-muted small">
              Busca un docente o estudiante que ya pertenece a tu academia (por
              su nombre o correo). Conservará su(s) rol(es) actuales; solo se
              le agrega el de propietario.
            </p>
            <Form.Group className="mb-2">
              <Form.Label className="fw-medium">Buscar usuario</Form.Label>
              <Form.Control
                type="text"
                placeholder="Escribe nombre o correo…"
                value={promoteQuery}
                onChange={(e) => {
                  setPromoteQuery(e.target.value);
                  setPromoteSelected(null);
                }}
                autoFocus
              />
            </Form.Group>

            {!promoteSelected && promoteQuery.trim() && (
              <ListGroup className="mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
                {promoteCandidates.length === 0 ? (
                  <ListGroup.Item className="text-muted small">
                    Sin coincidencias entre tus docentes y estudiantes.
                  </ListGroup.Item>
                ) : (
                  promoteCandidates.map((c) => (
                    <ListGroup.Item
                      key={c.id}
                      action
                      type="button"
                      onClick={() => {
                        setPromoteSelected(c);
                        setPromoteQuery(c.name || c.email);
                      }}
                    >
                      <div className="fw-medium">{c.name || c.email}</div>
                      {c.name && <div className="text-muted small">{c.email}</div>}
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
            )}

            {promoteSelected && (
              <Alert variant="light" className="border py-2 d-flex justify-content-between align-items-center">
                <span>
                  <i className="mdi mdi-account-check text-success me-1" aria-hidden="true" />
                  {promoteSelected.name || promoteSelected.email}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    setPromoteSelected(null);
                    setPromoteQuery("");
                  }}
                >
                  Cambiar
                </Button>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              style={{ minHeight: 44 }}
              onClick={resetPromote}
              disabled={promoteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ minHeight: 44 }}
              disabled={promoteMutation.isPending || !promoteSelected}
            >
              {promoteMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Asignar propietario"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export { PropietariosPage };
